import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Funnel, Clock, Certificate, ArrowRight, ShieldCheck } from "@phosphor-icons/react";
import Seo from "@/components/Seo";
import SeatsLeft from "@/components/SeatsLeft";
import PayIn4 from "@/components/PayIn4";

const STATES = ["ALL", "MD", "NJ", "DC", "VA", "AZ", "FL", "UT"];

export default function Training() {
  const [courses, setCourses] = useState([]);
  const [stateFilter, setStateFilter] = useState("ALL");
  const [categoryFilter, setCategoryFilter] = useState("ALL");

  useEffect(() => { api.get("/courses").then(({ data }) => setCourses(data)).catch(() => {}); }, []);

  const categories = useMemo(() => ["ALL", ...Array.from(new Set(courses.map(c => c.category)))], [courses]);

  const filtered = useMemo(() => courses.filter(c =>
    (stateFilter === "ALL" || c.state === stateFilter) &&
    (categoryFilter === "ALL" || c.category === categoryFilter)
  ), [courses, stateFilter, categoryFilter]);

  return (
    <div data-testid="training-page">
      <Seo
        title="Training Academy — State-Certified Firearms & Carry Permit Courses"
        description="State-certified Wear & Carry / CCW permit training, armed security officer certification, D.C. Special Police, first aid, and tactical fundamentals across MD, NJ, DC, VA, AZ, FL, and UT. Taught by our active-duty law-enforcement instructor team."
        path="/training"
      />
      <section className="border-b border-white/10 pt-24 pb-16 relative overflow-hidden">
        <img
          src="/img/brand/academy.png"
          alt=""
          aria-hidden
          className="absolute -right-16 -top-8 w-[360px] md:w-[480px] opacity-15 pointer-events-none select-none"
        />
        <div className="container-x grid grid-cols-12 gap-8 items-end relative">
          <div className="col-span-12 md:col-span-8">
            <div className="overline mb-4 flex items-center gap-3">
              <img src="/img/brand/academy.png" alt="Secrecy Executive Training Academy" className="h-14 w-auto" data-testid="training-hero-logo" />
              SECRECY EXECUTIVE TRAINING ACADEMY
            </div>
            <h1 className="font-heading text-5xl md:text-7xl tracking-tighter leading-[0.9]">
              State-certified courses.<br/><span className="text-[#FFD300]">Real-world training.</span>
            </h1>
          </div>
          <div className="col-span-12 md:col-span-4">
            <p className="text-neutral-400 leading-relaxed">
              {courses.length} courses across {STATES.length - 1} jurisdictions. From civilian fundamentals to advanced executive-protection certification.
            </p>
          </div>
        </div>
      </section>

      {/* eLearning sponsored promo */}
      <section className="border-b border-white/10 py-10 bg-gradient-to-r from-[#FFD300]/[0.07] via-transparent to-[#FFD300]/[0.07]" data-testid="training-elearning-promo">
        <div className="container-x grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 md:col-span-8">
            <div className="flex items-center gap-2 mb-3 overline">
              <ShieldCheck size={14} weight="duotone" className="text-[#FFD300]" />
              SPONSORED BY <span className="text-[#FFD300]">U.S. LAWSHIELD<sup>®</sup></span>
            </div>
            <h2 className="font-heading text-2xl md:text-3xl tracking-tight leading-tight mb-2">
              27 self-paced online courses — state permit training, marksmanship & legal carry.
            </h2>
            <p className="text-neutral-400 text-sm leading-relaxed">
              Compliant curriculum from Independent Program Attorneys. Take them on any device, any time.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 md:text-right">
            <Link
              to="/elearning"
              data-testid="training-elearning-cta"
              className="inline-flex items-center gap-2 bg-[#FFD300] text-black font-mono text-xs font-bold tracking-[0.18em] uppercase px-5 py-3 hover:bg-[#FFE149]"
            >
              Browse eLearning <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-x grid grid-cols-12 gap-8">
          <aside className="col-span-12 lg:col-span-3" data-testid="filters">
            <div className="sticky top-24 surface-card p-6">
              <div className="flex items-center gap-2 mb-6">
                <Funnel size={16} weight="duotone" className="text-[#FFD300]" />
                <div className="overline">Filter</div>
              </div>

              <div className="mb-6">
                <div className="label-mono mb-3">Jurisdiction</div>
                <div className="grid grid-cols-3 gap-2">
                  {STATES.map((s) => (
                    <button
                      key={s}
                      onClick={() => setStateFilter(s)}
                      data-testid={`filter-state-${s}`}
                      className={`state-tag py-2 transition-colors ${stateFilter === s ? '!bg-[#FFD300] !text-white !border-[#FFD300]' : 'hover:bg-white/10'}`}
                    >{s}</button>
                  ))}
                </div>
              </div>

              <div>
                <div className="label-mono mb-3">Category</div>
                <div className="space-y-2">
                  {categories.map((c) => (
                    <button
                      key={c}
                      onClick={() => setCategoryFilter(c)}
                      data-testid={`filter-cat-${c.replace(/\s/g, '-')}`}
                      className={`block w-full text-left font-mono text-xs uppercase tracking-[0.15em] py-2 px-3 transition-colors ${categoryFilter === c ? 'bg-[#FFD300] text-white' : 'hover:bg-white/5 text-neutral-300'}`}
                    >{c}</button>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          <div className="col-span-12 lg:col-span-9" data-testid="course-grid">
            <div className="overline mb-6">{filtered.length} COURSES MATCHED</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10">
              {filtered.map((c) => (
                <Link key={c.id} to={`/training/${c.slug}`} className="relative bg-[#0A0A0A] p-6 lift block group overflow-hidden" data-testid={`course-${c.slug}`}>
                  {c.slug === "multi-state-ccw-package" && (
                    <div
                      data-testid="best-value-ribbon"
                      className="absolute top-5 -right-12 rotate-45 bg-[#FFD300] text-black font-mono text-[10px] font-bold tracking-[0.2em] uppercase py-1.5 w-44 text-center shadow-lg z-10 pointer-events-none"
                    >
                      Best Value
                    </div>
                  )}
                  {c.is_special && (
                    <div
                      data-testid="special-ribbon"
                      className="absolute top-5 -right-12 rotate-45 bg-[#FF7A00] text-black font-mono text-[10px] font-bold tracking-[0.2em] uppercase py-1.5 w-44 text-center shadow-lg z-10 pointer-events-none"
                    >
                      {c.promo_label || "Special"}
                    </div>
                  )}
                  <div className="aspect-[4/3] overflow-hidden mb-5 bg-neutral-900">
                    {c.state || c.slug === "first-aid-cpr-aed" ? (
                      <div className="w-full h-full bg-gradient-to-br from-[#FFD300] via-[#fff] to-[#FFD300] flex items-center justify-center p-8">
                        <img src={c.image} alt={c.title} className="max-w-full max-h-full object-contain drop-shadow-xl" />
                      </div>
                    ) : (
                      <img src={c.image} alt={c.title} className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="code-badge">{c.code}</span>
                    {c.state && <span className="state-tag">{c.state}</span>}
                  </div>
                  <h3 className="font-heading text-xl tracking-tight leading-tight mb-3">{c.title}</h3>
                  <p className="text-neutral-400 text-sm leading-relaxed mb-5 line-clamp-2">{c.short_description}</p>
                  <div className="flex items-center justify-between text-xs text-neutral-400 mb-4">
                    <span className="flex items-center gap-1.5"><Clock size={14} /> {c.duration_hours}h</span>
                    <span className="flex items-center gap-1.5"><Certificate size={14} /> {c.level}</span>
                  </div>
                  <SeatsLeft next={c.next_session} className="mb-3 block" />
                  <div className="flex items-center justify-between border-t border-white/10 pt-4">
                    <div>
                      <div className="font-heading text-2xl">${c.price.toFixed(0)}</div>
                      <PayIn4 price={c.price} testId={`pay-in-4-${c.slug}`} />
                    </div>
                    <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#FFD300] group-hover:translate-x-1 transition-transform">Enroll →</span>
                  </div>
                </Link>
              ))}
              {filtered.length === 0 && (
                <div className="col-span-full bg-black p-12 text-center text-neutral-500 font-mono text-sm">No courses match your filters.</div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
